import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    ArrowLeft, Phone, Envelope, MapPin, Buildings, Stethoscope,
    Calendar, ChatCenteredText, PaperPlaneRight, NotePencil, CheckCircle, X,
    PaperPlaneTilt, Copy, LinkSimple, Sparkle, Trophy, ProhibitInset,
    Handshake, GraduationCap, IdentificationBadge, Briefcase,
    WhatsappLogo, Target, Pulse, Lightning, ClockClockwise, PhoneIncoming,
    PhoneSlash, Question, Clock, ListChecks, Plus, ChatTeardropDots,
    ArrowRight, EnvelopeSimple, PencilSimple,
    ShieldCheck, Warning,
} from "@phosphor-icons/react";
import { STATUS_PILLS, ROLE_LABEL } from "@/pages/coe/B2BLeadsList";

/* ===========================================================================
   B2BLeadDetail — iter98.99fi rebuild
   ---------------------------------------------------------------------------
   PES surface for a B2B partner-provider lead. Single-column narrative,
   2 steps total (was 3 + verification framework):

     Header        — clinic + code + status + role + source + age + Stage X/6
     Hero          — Next-action banner + quick-action toolbar (Call/WA/Email)
     Step 1        — Qualify & verify the doctor
                     · 3-question checklist (cases/month, planning, scanner)
                     · DCI Registration Certificate upload + verify checkbox
                     · Decide & move (Accept/Reject + pipeline + Resolve)
     Step 2        — Call & log (followup chips → unified activity timeline)
     Tasks         — Personal reminders ("Call Tuesday 4pm")
     Meta footer   — Created / updated / assignee (compact)

   Verification Framework (8 sections / 33 checkpoints / 6 auto-reject
   triggers) shipped in iter98.99fb has been retired. The Accept gate is
   now: qualifying_complete AND dci_certificate.verified.
   =========================================================================== */

// ──────────────────────────────────────────────────────────────────────────
// Static config
// ──────────────────────────────────────────────────────────────────────────

// Pipeline stage → 1-of-6 indicator. demo_scheduled keeps stage 4 because
// the demo is a sub-step of "qualified". Terminal states get null.
const STAGE_MAP = {
    new:              { step: 1, total: 6, label: "Untouched" },
    contacted:        { step: 2, total: 6, label: "Contacted" },
    discovery_done:   { step: 3, total: 6, label: "Discovery done" },
    qualified:        { step: 4, total: 6, label: "Qualified" },
    demo_scheduled:   { step: 4, total: 6, label: "Demo scheduled" },
    application_sent: { step: 5, total: 6, label: "Invite sent" },
    applied:          { step: 5, total: 6, label: "Application received" },
    approved:         { step: 6, total: 6, label: "Accepted — kickoff next" },
    application_incomplete: { step: 5, total: 6, label: "Awaiting missing docs" },
    handed_to_admin:  { step: 6, total: 6, label: "Admin review" },
    won:              null,
    lost:             null,
    unresponsive:     null,
};

// Source pills — same visual vocabulary as the Smile-Quiz page.
const B2B_SOURCE_PILLS = {
    "b2b-demo-request":           { label: "Demo request", Icon: Sparkle,        cls: "bg-violet-100 text-violet-700 border-violet-200" },
    "direct-apply":               { label: "Direct apply", Icon: PaperPlaneTilt, cls: "bg-amber-100 text-amber-800 border-amber-200" },
    "sales-invited-application":  { label: "Sales invite",  Icon: Handshake,     cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

// Outcome-style chips for the Step 2 call composer. Mirror of OUTCOME_CHIPS
// on the web-lead detail page but adapted to B2B (call / email / WA / SMS).
const FOLLOWUP_KINDS = [
    { id: "call",     label: "Call",     Icon: Phone,         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    { id: "email",    label: "Email",    Icon: EnvelopeSimple, cls: "bg-sky-100 text-sky-700 border-sky-200" },
    { id: "whatsapp", label: "WhatsApp", Icon: WhatsappLogo,  cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    { id: "sms",      label: "SMS",      Icon: ChatTeardropDots, cls: "bg-amber-100 text-amber-800 border-amber-200" },
];
const FOLLOWUP_LOOKUP = Object.fromEntries(FOLLOWUP_KINDS.map((c) => [c.id, c]));

// Reasons for the "Close as lost" picker. Each id is persisted as the
// transition reason and rendered in the activity timeline.
const LOSS_REASONS = [
    { id: "not_a_fit",       label: "Not a clinical / volume fit",  icon: ProhibitInset },
    { id: "budget",          label: "Budget mismatch",               icon: X },
    { id: "competitor",      label: "Going with a competitor",      icon: X },
    { id: "no_decision",     label: "No decision after qualified",  icon: ClockClockwise },
    { id: "wrong_contact",   label: "Wrong contact / role",         icon: X },
    { id: "other",           label: "Other",                         icon: PencilSimple },
];
const LOSS_REASON_LABEL = Object.fromEntries(LOSS_REASONS.map((r) => [r.id, r.label]));

// Pipeline transitions surfaced under Step 3, keyed by current status.
// Each entry tells the PES (a) the next status, (b) the button copy,
// (c) a "when to use this" hint, (d) emphasis styling.
const NEXT_STATUS_OPTIONS = {
    new: [
        { key: "contacted", label: "Mark contacted", hint: "You've spoken with the doctor on phone or had an email reply.", cls: "bg-violet-600 hover:bg-violet-700" },
    ],
    contacted: [
        { key: "discovery_done", label: "Discovery done", hint: "You ran a proper qualifying call — covered pain point, decision-maker, timeline.", cls: "bg-indigo-600 hover:bg-indigo-700" },
    ],
    discovery_done: [
        { key: "qualified", label: "Qualify lead", hint: "Doctor is a clinical + commercial fit. Ready for a demo or direct application.", cls: "bg-amber-600 hover:bg-amber-700" },
    ],
    qualified: [
        { key: "application_sent", label: "Send application invite", hint: "Doctor agreed to apply. The system emails a magic-link.", cls: "bg-cyan-700 hover:bg-cyan-800", emphasis: true },
    ],
    demo_scheduled: [
        { key: "qualified", label: "Mark back to qualified", hint: "Demo was rescheduled, postponed, or doctor wants more info first.", cls: "bg-secondary text-foreground hover:bg-secondary/80" },
    ],
    application_sent: [
        // Lost / unresponsive resolved by the dedicated resolve panel; no
        // forward action until the doctor submits the application.
    ],
    applied: [
        // iter98.99fb — PES no longer hands to admin. Approve/Reject lives
        // on the Application panel itself (gated by the Verification
        // Framework). Mark-incomplete handled by the resolve bar.
    ],
    approved: [
        { key: "demo_scheduled", label: "Schedule onboarding kickoff", hint: "Doctor account is live. Set up the platform walkthrough + agreement call.", cls: "bg-fuchsia-700 hover:bg-fuchsia-800", emphasis: true },
        { key: "won",            label: "Mark fully onboarded",        hint: "Kickoff happened, doctor is live and accepting referrals.",                  cls: "bg-emerald-700 hover:bg-emerald-800" },
    ],
    application_incomplete: [
        { key: "applied", label: "Docs received — back to applied", hint: "Doctor sent the missing documents. Resume the verification + accept flow.", cls: "bg-primary hover:bg-primary/90" },
    ],
    handed_to_admin: [],
    unresponsive: [
        { key: "new",       label: "Re-open lead",  hint: "Doctor reached back out and wants to talk again. Resets the follow-up tracker.", cls: "bg-primary hover:bg-primary/90" },
    ],
    won:  [],
    lost: [{ key: "new", label: "Re-open lead", hint: "Doctor changed their mind. Resets to top of funnel.", cls: "bg-primary hover:bg-primary/90" }],
};

// Status pill icon map (used inline in the page header).
const STATUS_ICONS = {
    new: Sparkle,
    contacted: ChatCenteredText,
    discovery_done: NotePencil,
    qualified: CheckCircle,
    demo_scheduled: Calendar,
    application_sent: PaperPlaneTilt,
    applied: PaperPlaneRight,
    approved: ShieldCheck,
    application_incomplete: Warning,
    handed_to_admin: Handshake,
    won: Trophy,
    lost: ProhibitInset,
    unresponsive: PhoneSlash,
};

// ──────────────────────────────────────────────────────────────────────────
// Pure helpers
// ──────────────────────────────────────────────────────────────────────────

function relAge(iso) {
    if (!iso) return null;
    try {
        const ms = Date.now() - new Date(iso).getTime();
        if (!Number.isFinite(ms) || ms < 0) return null;
        if (ms < 60_000) return "just now";
        const mins = Math.floor(ms / 60_000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(ms / 3_600_000);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(ms / 86_400_000);
        return `${days}d ago`;
    } catch { return null; }
}
function fullStamp(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return ""; }
}

// iter98.99fe — Prefix the partner-provider lead's contact name with "Dr."
// Idempotent: if the stored name already begins with "Dr." / "DR." / "dr."
// we don't double-prefix.
function formatDoctorName(name) {
    if (!name) return "";
    const trimmed = name.trim();
    if (/^dr\.?\s/i.test(trimmed)) return trimmed;
    return `Dr. ${trimmed}`;
}

function nextActionFor(lead) {
    if (!lead) return null;
    const s = lead.status;
    const attempts = lead.followup_attempts || 0;
    const qDone = !!lead.qualifying_complete;
    const dciVerified = !!lead.dci_certificate?.verified;
    const dciUploaded = !!lead.dci_certificate?.uploaded;

    if (s === "won")  return { label: "Doctor is fully onboarded. No further PES action.", tone: "emerald" };
    if (s === "lost") return { label: "Closed-lost. Re-open if the doctor reaches back out.", tone: "muted" };
    if (s === "unresponsive") return { label: `${attempts} follow-up attempts exhausted with no reply. Mark as lost or re-open if they respond.`, tone: "rose", jumpTo: "qualify-and-decide", jumpLabel: "Decide" };
    if (s === "handed_to_admin") return { label: "Admin is reviewing the application. No PES action until they decide.", tone: "muted" };
    if (s === "application_incomplete") return { label: "Doctor needs to send missing documents. Follow up via call + email + WhatsApp until received.", tone: "rose", jumpTo: "b2b-call-log", jumpLabel: "Log call" };
    if (s === "approved") {
        return { label: lead.demo_at
            ? `Doctor accepted. Onboarding kickoff is on ${fullStamp(lead.demo_at)}. After it happens, mark fully onboarded.`
            : "Doctor accepted. Schedule the onboarding kickoff (platform walkthrough + agreement).",
            tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Schedule kickoff" };
    }
    if (s === "applied") {
        if (lead.accept_ready) {
            return { label: "Qualifying complete + DCI certificate verified. Accept the application below.", tone: "emerald", jumpTo: "qualify-and-decide", jumpLabel: "Accept" };
        }
        if (!dciUploaded) return { label: "Doctor submitted the application. Upload their DCI certificate to unlock Accept.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Open Step 1" };
        if (!dciVerified) return { label: "DCI certificate uploaded. Tick \"Certificate verified\" once you've cross-checked it on the DCI portal.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Verify" };
        if (!qDone) return { label: "DCI verified. Finish the 3-question qualifying checklist to unlock Accept.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Finish checklist" };
        return { label: "Doctor submitted the application. Walk Step 1, then decide.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Open Step 1" };
    }
    if (s === "application_sent") return { label: "Magic-link invite sent. Nudge the doctor if they don't apply within 3 days.", tone: "violet", jumpTo: "b2b-call-log", jumpLabel: "Log nudge" };
    if (s === "demo_scheduled") return { label: lead.demo_at ? `Demo on ${fullStamp(lead.demo_at)}. Prep the deck and join 5 min early.` : "Demo confirmed. Prep the deck.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Update" };
    if (s === "qualified") return { label: "Doctor is qualified. Schedule a demo or send the application invite.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Decide" };
    if (s === "discovery_done") return { label: "Discovery complete. Qualify the lead if the fit looks right.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Qualify" };
    if (s === "contacted") {
        if (!qDone) return { label: "Run the qualifying checklist below before progressing the lead.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Open Step 1" };
        return { label: "Qualifying done — move to discovery_done after a proper call.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Move pipeline" };
    }
    // new
    return { label: "Call the doctor to introduce yourself, then complete the qualifying checklist.", tone: "primary", jumpTo: "qualify-and-decide", jumpLabel: "Open Step 1" };
}

const TONE_BG = {
    primary: "bg-primary/10 text-primary",
    violet:  "bg-violet-100 text-violet-700",
    rose:    "bg-rose-100 text-rose-700",
    emerald: "bg-emerald-100 text-emerald-700",
    muted:   "bg-secondary text-muted-foreground",
};

// ──────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────

export default function B2BLeadDetail({ scope = "sales" }) {
    const { id } = useParams();
    const navigate = useNavigate();

    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);  // qualifying checklist
    const [tasks, setTasks] = useState([]);
    const [appActing, setAppActing] = useState(false);
    const [demoBusy, setDemoBusy] = useState(false);
    const [showReassign, setShowReassign] = useState(false);

    // PES always returns to My Queue (per user feedback iter98.99fa).
    // Admin scope still routes to the admin pipeline.
    const backTo  = scope === "admin" ? "/admin/b2b-leads" : "/coe/leads";
    const backLbl = scope === "admin" ? "Back to B2B pipeline" : "Back to My Queue";

    const reload = () =>
        api.get(`/sales/b2b-leads/${id}`).then((r) => setLead(r.data));

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/sales/b2b-leads/${id}`).then((r) => setLead(r.data)),
            api.get(`/sales/qualifying-questions`).then((r) => setQuestions(r.data || [])).catch(() => setQuestions([])),
            api.get(`/sales/b2b-leads/${id}/tasks`).then((r) => setTasks(r.data || [])).catch(() => setTasks([])),
        ]).finally(() => setLoading(false));
    }, [id]);

    const reloadTasks = () =>
        api.get(`/sales/b2b-leads/${id}/tasks`).then((r) => setTasks(r.data || []));

    // ────── Step 3 helpers ──────
    const transitionTo = async ({ key, reason }) => {
        if (!reason || !reason.trim()) {
            toast.error("Add a short note explaining the move.");
            return false;
        }
        try {
            await api.put(`/sales/b2b-leads/${id}/status`, {
                status: key,
                reason: reason.trim(),
            });
            toast.success(`Status → ${STATUS_PILLS[key]?.label || key}`);
            await reload();
            return true;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update status");
            return false;
        }
    };

    const closeAsLost = async ({ reasonId, note }) => {
        if (!reasonId) {
            toast.error("Pick a reason before closing the lead.");
            return false;
        }
        const txt = `${LOSS_REASON_LABEL[reasonId]}${note?.trim() ? ` · ${note.trim()}` : ""}`;
        return transitionTo({ key: "lost", reason: txt });
    };

    const submitDemo = async ({ demo_at, demo_format, agenda }) => {
        if (demoBusy) return;
        setDemoBusy(true);
        try {
            await api.post(`/sales/b2b-leads/${id}/schedule-demo`, { demo_at, demo_format, agenda });
            toast.success("Demo scheduled — calendar invite emailed");
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to schedule demo");
        } finally {
            setDemoBusy(false);
        }
    };

    const sendInvite = async ({ note }) => {
        try {
            const r = await api.post(`/sales/b2b-leads/${id}/send-application`, { custom_note: (note || "").trim() });
            toast.success("Application invite emailed");
            await reload();
            return r.data;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send invite");
            return null;
        }
    };

    const actOnApplication = async (action) => {
        const appId = lead?.application?.id;
        if (!appId || appActing) return;
        setAppActing(true);
        try {
            await api.put(`/admin/applications/${appId}/${action}`);
            toast.success(action === "approve"
                ? "Approved — doctor account is live"
                : "Application rejected");
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || `Failed to ${action}`);
        } finally {
            setAppActing(false);
        }
    };

    // ────── derived ──────
    const stage = lead ? STAGE_MAP[lead.status] : null;
    const status = lead ? (STATUS_PILLS[lead.status] || STATUS_PILLS.new) : null;
    const source = lead ? (B2B_SOURCE_PILLS[lead.source] || { label: lead.source || "Unknown", Icon: Question, cls: "bg-secondary text-muted-foreground border-border" }) : null;
    const nextAction = useMemo(() => nextActionFor(lead), [lead]);
    const isTerminal = lead && (lead.status === "won" || lead.status === "lost");
    const nextOptions = lead ? (NEXT_STATUS_OPTIONS[lead.status] || []) : [];

    if (loading) return <div className="text-muted-foreground p-4">Loading…</div>;
    if (!lead) return <div className="text-muted-foreground p-4">Lead not found.</div>;

    const SourceIcon = source.Icon;

    return (
        <div data-testid="b2b-lead-detail">
            <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6"
                data-testid="b2b-lead-back">
                <ArrowLeft size={14} /> {backLbl}
            </Link>

            {/* ────────── Header card ────────── */}
            <div className="bg-card border border-border rounded-2xl p-6 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="b2b-detail-clinic">
                                {lead.clinic}
                            </h1>
                            {lead.code && (
                                <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-secondary/60 text-muted-foreground border border-border"
                                    data-testid="b2b-lead-detail-code">
                                    {lead.code}
                                </span>
                            )}
                            <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border bg-secondary text-muted-foreground border-border font-semibold inline-flex items-center gap-1">
                                <Stethoscope size={11} weight="fill" />
                                {ROLE_LABEL[lead.role] || lead.role}
                            </span>
                            {/* iter98.99fe — Removed (per user request, redundant with
                                Pending Review pill + the Step-1 Verification UI):
                                · APPLIED lead-status pill
                                · DIRECT APPLY source chip
                                · STAGE 5 OF 6 · APPLICATION RECEIVED badge */}
                            {/* iter98.99fd — Application code + review pill folded
                                into the header so the legacy Application panel
                                can be removed entirely. */}
                            {lead.application?.code && (
                                <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-secondary/60 text-muted-foreground border border-border"
                                    data-testid="b2b-application-code">
                                    {lead.application.code}
                                </span>
                            )}
                            {lead.application && (
                                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold ${
                                    (APP_STATUS_PILL[lead.application.status] || APP_STATUS_PILL.pending).cls
                                }`}
                                    data-testid={`b2b-application-status-${lead.application.status || "pending"}`}>
                                    {(APP_STATUS_PILL[lead.application.status] || APP_STATUS_PILL.pending).label}
                                </span>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-muted-foreground">
                            {/* iter98.99fe — Always honorific "Dr." since this is a
                                B2B partner-provider lead. If the doctor's stored
                                name already starts with Dr. (any casing), we don't
                                double-prefix. */}
                            <span className="flex items-center gap-1.5"><Stethoscope size={13} /> {formatDoctorName(lead.contact_name)}</span>
                            <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone size={13} /> {lead.phone}</a>
                            {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-primary"><Envelope size={13} /> {lead.email}</a>}
                            <span className="flex items-center gap-1.5"><MapPin size={13} /> {lead.city || "—"}</span>
                            {lead.created_at && (
                                <span className="flex items-center gap-1.5"
                                    title={`Submitted ${fullStamp(lead.created_at)}`}
                                    data-testid="b2b-lead-detail-submitted">
                                    <ClockClockwise size={13} />
                                    <span>
                                        Submitted <span className="font-medium text-foreground">{relAge(lead.created_at) || "—"}</span>
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>

                    {scope === "admin" && !isTerminal && (
                        <Button
                            size="sm" variant="outline"
                            onClick={() => setShowReassign(true)}
                            className="rounded-full"
                            data-testid="b2b-reassign-btn">
                            Reassign to another rep
                        </Button>
                    )}
                </div>

                {/* Practice intel mini-grid (mirrors the Smile-Quiz "answers grid").
                    iter98.99fd — Folds in the application credentials so PES
                    sees everything in ONE compact grid; no separate panel below. */}
                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="b2b-detail-intel-grid">
                    {lead.application && (
                        <>
                            <IntelCell label="Degree" value={lead.application.degree || "—"} />
                            <IntelCell label="Years in practice" value={lead.application.years_in_practice != null ? `${lead.application.years_in_practice} yrs` : "—"} />
                            <IntelCell label="Council reg. #" value={lead.application.council_registration_number || "—"} mono />
                            <IntelCell label="State council" value={lead.application.state_council || "—"} />
                        </>
                    )}
                    <IntelCell
                        label="Cases / month"
                        value={(lead.application && lead.application.cases_per_month) || lead.cases_per_month || "—"}
                    />
                    <IntelCell label="Current brand" value={lead.current_aligner_brand || "—"} />
                    {/* iter98.99ff — "Assigned" now prefers the PES who claimed
                        the lead (hub_assignee_name) over the auto-assigned
                        sales rep (assigned_sales_name). PES = operational
                        owner; sales rep = commercial owner. Most leads
                        have only one set. */}
                    <IntelCell
                        label="Assigned to"
                        value={lead.hub_assignee_name || lead.assigned_sales_name || "Unassigned"}
                    />
                    <IntelCell
                        label="Follow-ups"
                        value={
                            lead.status === "unresponsive"
                                ? `${lead.followup_attempts || 0} attempts · unresponsive`
                                : `${lead.followup_attempts || 0} of 3 attempts`
                        }
                        tone={lead.status === "unresponsive" ? "rose" : (lead.followup_attempts >= 2 ? "amber" : "neutral")}
                    />
                </div>

                {/* iter98.99fd — Application narrative content (Why Invisique +
                    Doctor's note) folded into the header card so everything
                    about the doctor lives in ONE section. */}
                {lead.application?.why_invisique && (
                    <div className="mt-4 rounded-xl bg-secondary/30 border border-border/60 p-4" data-testid="b2b-why-invisique">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Why Invisique™</div>
                        <p className="text-sm whitespace-pre-wrap">{lead.application.why_invisique}</p>
                    </div>
                )}
                {lead.message
                    && (!lead.application?.why_invisique
                        || lead.message.trim().toLowerCase() !== lead.application.why_invisique.trim().toLowerCase())
                    && (
                        <div className="mt-3 rounded-xl bg-amber-50/40 border border-amber-200 p-4" data-testid="b2b-doctor-note">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-amber-900 mb-1 flex items-center gap-1">
                                <ChatCenteredText size={11} weight="fill" /> Doctor&apos;s note (from request form)
                            </div>
                            <p className="text-sm whitespace-pre-wrap text-amber-950">{lead.message}</p>
                        </div>
                    )
                }
            </div>

            {/* iter98.99fd — Application Submitted panel removed. Its content
                (credentials grid + why-invisique + doctor's note) is now part
                of the header card above so the PES sees everything about the
                doctor in ONE section. The decision UI (Accept/Reject + the
                Step-1-gated banner) lives inside Step 1 below. */}

            {/* iter98.99fi — Step 1 now bundles Qualifying + DCI cert + the
                full Decide-&-move block (Accept/Reject + pipeline transitions
                + Resolve). Verification framework is gone — replaced by the
                simpler qualifying-complete + DCI-verified Accept gate. */}
            <QualifyAndDecidePanel
                lead={lead}
                scope={scope}
                questions={questions}
                nextOptions={nextOptions}
                appActing={appActing}
                onAct={actOnApplication}
                onTransition={transitionTo}
                onCloseLost={closeAsLost}
                onSubmitDemo={submitDemo}
                onSendInvite={sendInvite}
                onSaved={reload}
                demoBusy={demoBusy}
                isTerminal={isTerminal}
            />

            {/* ────────── Step 2 · Call & log ────────── */}
            <CallAndLogPanel
                lead={lead}
                onLogged={reload}
            />

            {/* ────────── Step 3 · Tasks ────────── */}
            <TasksPanel
                leadId={lead.id}
                tasks={tasks}
                onChange={reloadTasks}
            />

            {/* ────────── Footer meta ────────── */}
            <MetaFooter lead={lead} />

            {showReassign && scope === "admin" && (
                <ReassignDialog lead={lead} onClose={() => setShowReassign(false)} onDone={reload} />
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Header atoms
// ──────────────────────────────────────────────────────────────────────────

function IntelCell({ label, value, tone = "neutral" }) {
    const toneCls = {
        neutral: "bg-secondary/40",
        amber:   "bg-amber-50 border border-amber-200 text-amber-900",
        rose:    "bg-rose-50 border border-rose-200 text-rose-900",
    }[tone] || "bg-secondary/40";
    return (
        <div className={`rounded-lg px-3 py-2 ${toneCls}`}>
            <div className="text-muted-foreground text-[10px] uppercase tracking-wider font-bold">{label}</div>
            <div className="font-medium mt-0.5 text-sm">{value}</div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 1 · Qualifying checklist
// ──────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────
// Step 1 · Qualify & Decide (iter98.99fi — full rebuild)
// ──────────────────────────────────────────────────────────────────────────
//
// Step 1 is now the single decision surface for the PES on this lead. It
// holds (in order):
//   1. Qualifying questions (3 selects: cases/month, planning preference,
//      I/O scanner in-house).
//   2. DCI Registration Certificate — upload + verify checkbox.
//   3. Accept-readiness banner.
//   4. The full Decide-&-move block that used to live in old Step 2:
//      Accept / Reject + pipeline transitions + Resolve (mark incomplete,
//      mark unresponsive, close as lost).
//
// The Verification Framework (8 categories × 33 checkpoints + 6 auto-reject
// triggers) shipped in iter98.99fb has been completely retired. The simpler
// gate is: qualifying_complete && dci_certificate.verified.

function QualifyAndDecidePanel({
    lead, scope, questions, nextOptions, appActing, onAct,
    onTransition, onCloseLost, onSubmitDemo, onSendInvite,
    onSaved, demoBusy, isTerminal,
}) {
    return (
        <div className="bg-card border border-border rounded-2xl p-6 mb-6"
            data-testid="b2b-qualify-decide-panel"
            id="qualify-and-decide">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary/60">
                    Step 1
                </span>
                <ShieldCheck size={20} weight="duotone" className="text-primary" />
                <h3 className="font-display text-lg font-semibold">Qualify &amp; verify the doctor</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Capture the 3 qualifying facts, confirm the DCI registration certificate, then accept or move the lead. Pipeline transitions land in the activity timeline.
            </p>

            <QualifyingChecklistInner lead={lead} questions={questions} onSaved={onSaved} />

            <div className="mt-6 pt-6 border-t border-border">
                <DciCertificateInner lead={lead} onSaved={onSaved} />
            </div>

            {!isTerminal && (
                <div className="mt-6 pt-6 border-t border-border">
                    <DecisionInner
                        lead={lead}
                        scope={scope}
                        nextOptions={nextOptions}
                        appActing={appActing}
                        onAct={onAct}
                        onTransition={onTransition}
                        onCloseLost={onCloseLost}
                        onSubmitDemo={onSubmitDemo}
                        onSendInvite={onSendInvite}
                        demoBusy={demoBusy}
                    />
                </div>
            )}
        </div>
    );
}

function QualifyingChecklistInner({ lead, questions, onSaved }) {
    const initial = lead.qualifying_answers || {};
    const [answers, setAnswers] = useState(initial);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setAnswers(lead.qualifying_answers || {});
    }, [lead.id, lead.qualifying_updated_at]);

    if (!questions || questions.length === 0) return null;

    const answeredCount = questions.filter((q) => (answers[q.id] || "").trim()).length;
    const total = questions.length;
    const complete = answeredCount === total;
    const dirty = JSON.stringify(answers) !== JSON.stringify(initial);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/sales/b2b-leads/${lead.id}/qualifying`, { answers });
            toast.success("Qualifying checklist saved");
            await onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="b2b-qualifying-panel">
            <div className="flex items-center gap-2 mb-3">
                <ListChecks size={15} weight="duotone" className="text-primary" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wide">Qualifying questions</h4>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold ${
                    complete
                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : "bg-amber-100 text-amber-800 border-amber-200"
                }`}
                    data-testid="b2b-qualifying-progress">
                    {answeredCount}/{total} {complete ? "complete" : "answered"}
                </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                {questions.map((q) => (
                    <div key={q.id} data-testid={`b2b-qualifying-${q.id}`}>
                        <Label className="text-xs font-semibold">{q.label}</Label>
                        <select
                            value={answers[q.id] || ""}
                            onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                            className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            data-testid={`b2b-qualifying-${q.id}-select`}
                        >
                            <option value="">— Pick one —</option>
                            {(q.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>

            <div className="flex justify-end mt-3">
                <Button
                    size="sm"
                    onClick={save}
                    disabled={busy || !dirty}
                    data-testid="b2b-qualifying-save"
                >
                    {busy ? "Saving…" : (dirty ? "Save qualifying" : "Saved")}
                </Button>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 1 · DCI Registration Certificate (upload + verify)
// ──────────────────────────────────────────────────────────────────────────

function DciCertificateInner({ lead, onSaved }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const cert = lead.dci_certificate || {};

    const triggerPick = () => {
        if (uploading || verifying) return;
        fileRef.current?.click();
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        // Always reset the input so the same file can be picked twice.
        if (e.target) e.target.value = "";
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("File too large (max 8 MB)");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            await api.post(`/sales/b2b-leads/${lead.id}/dci-certificate`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Certificate uploaded");
            await onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const toggleVerified = async () => {
        if (verifying || !cert.uploaded) return;
        setVerifying(true);
        try {
            await api.put(`/sales/b2b-leads/${lead.id}/dci-certificate/verify`);
            toast.success(cert.verified ? "Verification revoked" : "Certificate verified");
            await onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update");
        } finally {
            setVerifying(false);
        }
    };

    // Build the absolute download URL off the configured backend URL — the
    // axios `api` client wraps the path automatically, but anchor `href`
    // needs the full origin. Falls back gracefully to the same-origin path.
    const downloadHref = cert.url
        ? `${process.env.REACT_APP_BACKEND_URL || ""}${cert.url}`
        : null;

    return (
        <div data-testid="b2b-dci-cert-panel">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <IdentificationBadge size={15} weight="duotone" className="text-primary" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wide">DCI Registration Certificate</h4>
                {cert.verified ? (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold bg-emerald-100 text-emerald-700 border-emerald-200"
                        data-testid="b2b-dci-verified-pill">
                        Verified
                    </span>
                ) : cert.uploaded ? (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold bg-amber-100 text-amber-800 border-amber-200"
                        data-testid="b2b-dci-pending-pill">
                        Awaiting verification
                    </span>
                ) : (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold bg-secondary text-muted-foreground border-border"
                        data-testid="b2b-dci-missing-pill">
                        Not uploaded
                    </span>
                )}
            </div>

            <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={handleUpload}
                data-testid="b2b-dci-file-input"
            />

            {/* Upload row */}
            <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-4">
                {cert.uploaded ? (
                    <div className="flex items-start gap-3 flex-wrap"
                        data-testid="b2b-dci-uploaded-row">
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 grid place-items-center text-primary">
                            <IdentificationBadge size={18} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate" data-testid="b2b-dci-filename">
                                {cert.filename || "DCI certificate"}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                Uploaded {cert.uploaded_at ? relAge(cert.uploaded_at) : "—"}
                                {cert.uploaded_by_name && ` · by ${cert.uploaded_by_name}`}
                                {cert.verified && cert.verified_by_name && ` · verified by ${cert.verified_by_name}`}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                            {downloadHref && (
                                <a
                                    href={downloadHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                                    data-testid="b2b-dci-view-link">
                                    <LinkSimple size={12} />
                                    View
                                </a>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={triggerPick}
                                disabled={uploading}
                                data-testid="b2b-dci-replace-btn">
                                {uploading ? "Uploading…" : "Replace"}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-secondary grid place-items-center text-muted-foreground">
                            <PaperPlaneTilt size={18} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">Upload certificate shared by doctor</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                PDF / JPG / PNG / WebP / HEIC · max 8 MB
                            </div>
                        </div>
                        <Button
                            size="sm"
                            onClick={triggerPick}
                            disabled={uploading}
                            data-testid="b2b-dci-upload-btn">
                            {uploading ? "Uploading…" : "Choose file"}
                        </Button>
                    </div>
                )}
            </div>

            {/* Verify checkbox */}
            <label
                className={`mt-3 flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    cert.uploaded
                        ? cert.verified
                            ? "bg-emerald-50/60 border-emerald-200"
                            : "bg-background border-border hover:bg-secondary/40"
                        : "bg-secondary/30 border-border opacity-60 cursor-not-allowed"
                }`}
                data-testid="b2b-dci-verify-row">
                <input
                    type="checkbox"
                    checked={!!cert.verified}
                    disabled={!cert.uploaded || verifying}
                    onChange={toggleVerified}
                    className="mt-0.5 h-4 w-4 accent-emerald-600"
                    data-testid="b2b-dci-verify-toggle"
                />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                        Certificate verified <span aria-hidden>✅</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        {cert.uploaded
                            ? "I cross-checked the registration number on the DCI portal and the certificate is genuine + current."
                            : "Upload the certificate first to enable this checkbox."}
                    </div>
                </div>
                {verifying && (
                    <span className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground self-center" data-testid="b2b-dci-verify-busy">
                        Saving…
                    </span>
                )}
            </label>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 2 · Call & log (followup chips + unified activity timeline)
// ──────────────────────────────────────────────────────────────────────────

function CallAndLogPanel({ lead, onLogged }) {
    const [kind, setKind] = useState(null);
    const [body, setBody] = useState("");
    const [responded, setResponded] = useState(false);
    const [busy, setBusy] = useState(false);

    // Composer also supports a plain "Add note" path (no kind selected).
    const submit = async () => {
        const text = body.trim();
        if (!text && !kind) return;
        setBusy(true);
        try {
            if (kind) {
                await api.post(`/sales/b2b-leads/${lead.id}/log-followup`, {
                    kind, body: text, responded,
                });
                toast.success(`Logged ${FOLLOWUP_LOOKUP[kind].label}${responded ? " · Doctor responded ✓" : ""}`);
            } else {
                await api.post(`/sales/b2b-leads/${lead.id}/note`, { body: text });
                toast.success("Note added");
            }
            setBody("");
            setKind(null);
            setResponded(false);
            await onLogged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save");
        } finally {
            setBusy(false);
        }
    };

    const notes = useMemo(() => {
        return [...(lead.notes || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }, [lead]);

    return (
        <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="b2b-log-panel" id="b2b-call-log">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary/60">
                    Step 2
                </span>
                <Pulse size={20} weight="duotone" className="text-primary" />
                <h3 className="font-display text-lg font-semibold">Call &amp; log</h3>
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Each call attempt counts toward the 3-strike unresponsive rule
                </span>
            </div>

            {/* Kind chips */}
            <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="b2b-followup-chips">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground pr-1">
                    Log an attempt
                </span>
                {FOLLOWUP_KINDS.map((c) => {
                    const active = kind === c.id;
                    return (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => setKind(active ? null : c.id)}
                            className={`inline-flex items-center gap-1 px-2.5 h-7 text-[11.5px] font-semibold rounded-full border transition-all ${
                                active ? `${c.cls} shadow-sm ring-2 ring-offset-1 ring-current/20`
                                       : "bg-secondary/40 border-border text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                            }`}
                            data-testid={`b2b-followup-${c.id}`}
                            aria-pressed={active}>
                            <c.Icon size={11} weight="fill" />
                            {c.label}
                        </button>
                    );
                })}
                {kind && (
                    <label className="ml-auto inline-flex items-center gap-2 text-[12px] cursor-pointer select-none"
                        data-testid="b2b-followup-responded-toggle">
                        <input
                            type="checkbox"
                            checked={responded}
                            onChange={(e) => setResponded(e.target.checked)}
                            className="h-3.5 w-3.5"
                        />
                        Doctor responded
                    </label>
                )}
            </div>

            <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                placeholder={kind
                    ? `What happened on the ${FOLLOWUP_LOOKUP[kind].label.toLowerCase()}? (optional)`
                    : "Add a quick note — call summary, blocker, next step…"}
                className="mb-3"
                data-testid="b2b-note-input"
            />
            <div className="flex justify-end">
                <Button
                    size="sm"
                    onClick={submit}
                    disabled={busy || (!kind && !body.trim())}
                    data-testid="b2b-post-note-btn">
                    <PaperPlaneRight size={14} className="mr-1.5" />
                    {busy ? "Saving…" : (kind ? "Log attempt" : "Add note")}
                </Button>
            </div>

            <ActivityTimeline notes={notes} />
        </div>
    );
}

function ActivityTimeline({ notes }) {
    if (!notes || notes.length === 0) {
        return (
            <div className="mt-5 pt-5 border-t border-border text-sm text-muted-foreground" data-testid="b2b-activity-empty">
                No activity yet. Notes + status transitions will appear here.
            </div>
        );
    }
    return (
        <div className="mt-5 pt-5 border-t border-border" data-testid="b2b-activity-log">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-3">
                Activity timeline ({notes.length})
            </div>
            <ul className="space-y-3">
                {notes.map((n) => (
                    <li key={n.id} className="flex gap-3" data-testid={`b2b-note-${n.id}`}>
                        <div className={`shrink-0 w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${
                            n.action === "status_changed" ? "bg-violet-100 text-violet-700" :
                            n.action === "auto_unresponsive" ? "bg-rose-100 text-rose-700" :
                            n.action === "demo_scheduled" ? "bg-fuchsia-100 text-fuchsia-700" :
                            n.action === "followup_attempt" ? "bg-amber-100 text-amber-800" :
                            n.action === "qualifying_updated" ? "bg-emerald-100 text-emerald-700" :
                            n.action === "task_created" ? "bg-sky-100 text-sky-700" :
                            "bg-secondary text-muted-foreground"
                        }`}>
                            {n.action === "status_changed" ? <Lightning size={13} weight="fill" /> :
                             n.action === "auto_unresponsive" ? <PhoneSlash size={13} weight="fill" /> :
                             n.action === "demo_scheduled" ? <Calendar size={13} weight="fill" /> :
                             n.action === "followup_attempt" ? <PhoneIncoming size={13} weight="fill" /> :
                             n.action === "qualifying_updated" ? <ListChecks size={13} weight="fill" /> :
                             n.action === "task_created" ? <ListChecks size={13} weight="fill" /> :
                             n.action === "task_completed" ? <CheckCircle size={13} weight="fill" /> :
                             (n.actor_name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="text-xs font-semibold">
                                    {n.actor_name || (n.actor_role === "public" ? "Public form" : n.actor_role === "system" ? "System" : "—")}
                                    <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                                        {(n.action || "note").replace(/_/g, " ")}
                                    </span>
                                </div>
                                <div className="text-[11px] text-muted-foreground tabular-nums">
                                    {n.created_at ? new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                                </div>
                            </div>
                            {n.body && <p className="text-sm mt-0.5 whitespace-pre-wrap break-words text-foreground/90">{n.body}</p>}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 1 (inner) · Decide & move — Accept/Reject + pipeline + Resolve
// ──────────────────────────────────────────────────────────────────────────
//
// iter98.99fi — Rebadged from DecisionPanel → DecisionInner because it
// now renders inside the Step 1 card (no outer chrome). Verification
// banner removed: Accept now reads off `lead.accept_ready` (computed
// server-side from qualifying_complete + dci_certificate.verified).

function DecisionInner({ lead, scope, nextOptions, appActing, onAct, onTransition, onCloseLost, onSubmitDemo, onSendInvite, demoBusy }) {
    // iter98.99fc — Renamed from PipelinePanel and augmented. Single unified
    // decision block: Accept/Reject (when applied) + pipeline transitions
    // + Resolve actions (mark incomplete/unresponsive/lost) + inline demo
    // scheduling. The verification decision banner that used to live in
    // the Application panel now lives here, right next to the buttons it
    // gates — so the PES never has to scroll up to reconcile state.

    const [activeTransition, setActiveTransition] = useState(null);  // {key, label, hint}
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    // Resolve sub-panels
    const [showLost, setShowLost] = useState(false);
    const [lossReason, setLossReason] = useState(null);
    const [lossNote, setLossNote] = useState("");
    const [lossBusy, setLossBusy] = useState(false);

    const [showUnresponsive, setShowUnresponsive] = useState(false);
    const [urNote, setUrNote] = useState("");

    // iter98.99fb — Mark-incomplete sub-panel (PES chases missing docs).
    const [showIncomplete, setShowIncomplete] = useState(false);
    const [missDocs, setMissDocs] = useState([]);
    const [missNote, setMissNote] = useState("");
    const [missBusy, setMissBusy] = useState(false);
    const [customDoc, setCustomDoc] = useState("");

    const status = lead.status;
    const canScheduleDemo = ["qualified", "demo_scheduled", "discovery_done", "contacted", "approved"].includes(status);
    const canSendInvite   = ["qualified", "demo_scheduled", "application_sent"].includes(status);
    const canMarkIncomplete = ["applied", "approved"].includes(status);

    // iter98.99fi — Accept gate simplified. Reads `accept_ready` from the
    // server (= qualifying_complete AND dci_certificate.verified). No more
    // verification framework, no must-have/red-flag accounting.
    const application = lead.application;
    const appStatus = application?.status || null;
    const canActOnApp = !!application
        && appStatus === "pending"
        && ["admin", "coe", "sales"].includes(scope);
    const acceptReady = !!lead.accept_ready;
    const dciVerified = !!lead.dci_certificate?.verified;
    const qDone       = !!lead.qualifying_complete;
    const acceptEnabled = canActOnApp && acceptReady;
    let gateHint = "Ready to accept — qualifying complete and DCI certificate verified.";
    if (!qDone && !dciVerified) gateHint = "Finish the 3 qualifying questions and verify the DCI certificate above to unlock Accept.";
    else if (!qDone)            gateHint = "Finish the 3 qualifying questions above to unlock Accept.";
    else if (!dciVerified)      gateHint = "Tick the DCI certificate \"Verified\" checkbox above to unlock Accept.";

    const submitTransition = async () => {
        if (!activeTransition) return;
        setBusy(true);
        const ok = await onTransition({ key: activeTransition.key, reason });
        setBusy(false);
        if (ok) {
            setActiveTransition(null);
            setReason("");
        }
    };

    const submitLost = async () => {
        if (!lossReason) {
            toast.error("Pick a reason");
            return;
        }
        setLossBusy(true);
        const ok = await onCloseLost({ reasonId: lossReason, note: lossNote });
        setLossBusy(false);
        if (ok) {
            setShowLost(false);
            setLossReason(null);
            setLossNote("");
        }
    };

    const submitUnresponsive = async () => {
        setBusy(true);
        const ok = await onTransition({
            key: "unresponsive",
            reason: `Marked unresponsive manually${urNote.trim() ? ` · ${urNote.trim()}` : ""}`,
        });
        setBusy(false);
        if (ok) {
            setShowUnresponsive(false);
            setUrNote("");
        }
    };

    // iter98.99fb — Fire the mark-incomplete endpoint (lists missing docs
    // + emails the doctor + flips status to "application_incomplete").
    const submitIncomplete = async () => {
        if (missDocs.length === 0) {
            toast.error("Pick at least one missing doc");
            return;
        }
        setMissBusy(true);
        try {
            await api.post(`/sales/b2b-leads/${lead.id}/mark-incomplete`, {
                missing_docs: missDocs,
                note: missNote.trim(),
            });
            toast.success("Doctor emailed with the missing-doc checklist");
            setShowIncomplete(false);
            setMissDocs([]);
            setMissNote("");
            setCustomDoc("");
            // Reuse the parent's transition machinery to reload the lead.
            await onTransition({ key: lead.status, reason: "" }).catch(() => {});
            // The above will fail validation (empty reason); fall back to a clean reload.
            window.location.reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to mark incomplete");
        } finally {
            setMissBusy(false);
        }
    };

    const COMMON_MISSING_DOCS = [
        "Updated council registration certificate",
        "Specialty degree certificate (MDS)",
        "Clinic registration / license",
        "Recent clinic photos",
        "Signed partner provider agreement",
        "Practice address proof",
    ];
    const toggleDoc = (doc) =>
        setMissDocs((cur) => cur.includes(doc) ? cur.filter((d) => d !== doc) : [...cur, doc]);

    return (
        <div data-testid="b2b-decide-inner" id="b2b-decision">
            <div className="flex items-center gap-2 mb-1">
                <Lightning size={15} weight="duotone" className="text-primary" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wide">Decide &amp; move</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                {canActOnApp
                    ? "Accept once Step 1 above is complete — or reject. Pipeline transitions land in the activity timeline."
                    : "Every transition asks for a short reason. That reason lands in the activity timeline so future PES (and audits) know why this lead moved."}
            </p>

            {/* iter98.99fi — Application Accept/Reject block (only when there's
                a pending application). Gating + hint copy now reads off
                lead.accept_ready (qualifying_complete + dci_certificate.verified). */}
            {canActOnApp && (
                <div className="rounded-xl border border-border bg-secondary/20 p-4 mb-4" data-testid="b2b-decision-app-block">
                    <div className={`rounded-lg p-3 mb-3 border flex items-center gap-2 text-sm font-semibold ${
                        acceptReady
                            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                            : "bg-amber-50 border-amber-200 text-amber-900"
                    }`}
                        data-testid={`b2b-accept-ready-${acceptReady ? "yes" : "no"}`}>
                        {acceptReady
                            ? <ShieldCheck size={15} weight="fill" />
                            : <ListChecks size={15} weight="duotone" />}
                        <span>{gateHint}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button onClick={() => onAct("approve")} disabled={appActing || !acceptEnabled}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-emerald-200 disabled:text-emerald-700"
                            title={acceptEnabled
                                ? "Approve — creates the doctor account, then schedule the kickoff."
                                : gateHint}
                            data-testid="b2b-application-approve">
                            <CheckCircle size={14} weight="fill" className="mr-1.5" />
                            {appActing ? "Approving…" : "Accept — create doctor account"}
                        </Button>
                        <Button onClick={() => onAct("reject")} disabled={appActing}
                            variant="outline"
                            className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                            data-testid="b2b-application-reject">
                            <X size={14} weight="bold" className="mr-1.5" />
                            Reject
                        </Button>
                    </div>
                </div>
            )}

            {/* Demo scheduling */}
            {canScheduleDemo && (
                <div className="mb-4">
                    <DemoSchedulingPanel
                        lead={lead}
                        busy={demoBusy}
                        onSubmit={onSubmitDemo}
                    />
                </div>
            )}

            {/* Application invite */}
            {canSendInvite && (
                <div className="mb-4">
                    <InvitePanel lead={lead} onSend={onSendInvite} />
                </div>
            )}

            {/* Inline reason form for the picked transition */}
            {activeTransition ? (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-4" data-testid="b2b-transition-form">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-sm font-semibold text-primary inline-flex items-center gap-1.5">
                            <Lightning size={15} weight="fill" /> {activeTransition.label}
                        </div>
                        <button
                            type="button"
                            onClick={() => { setActiveTransition(null); setReason(""); }}
                            className="text-xs text-muted-foreground hover:text-foreground"
                            data-testid="b2b-transition-cancel">
                            Cancel
                        </button>
                    </div>
                    <p className="text-[12px] text-muted-foreground mb-3">{activeTransition.hint}</p>
                    <Label className="text-xs">Reason / short note</Label>
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        placeholder="e.g. 'Doctor confirmed they want to switch from Invisalign — ready for a discovery call.'"
                        className="mt-1 text-sm"
                        data-testid="b2b-transition-reason"
                    />
                    <div className="flex justify-end mt-3">
                        <Button
                            size="sm"
                            onClick={submitTransition}
                            disabled={busy || !reason.trim()}
                            className={activeTransition.cls || ""}
                            data-testid="b2b-transition-confirm">
                            {busy ? "Moving…" : "Confirm move"}
                        </Button>
                    </div>
                </div>
            ) : (
                nextOptions.length > 0 && (
                    <div className="space-y-2 mb-4">
                        {nextOptions.map((opt) => (
                            <div key={opt.key} className="rounded-xl border border-border hover:border-primary/30 transition-colors p-3 flex items-start gap-3"
                                data-testid={`b2b-transition-row-${opt.key}`}>
                                <Button
                                    onClick={() => setActiveTransition(opt)}
                                    className={`rounded-full ${opt.cls || ""} ${opt.emphasis ? "shadow-md" : ""}`}
                                    size="sm"
                                    data-testid={`b2b-transition-${opt.key}`}>
                                    {opt.label}
                                </Button>
                                <p className="text-[12px] text-muted-foreground flex-1 leading-snug">{opt.hint}</p>
                            </div>
                        ))}
                    </div>
                )
            )}

            {/* Resolve actions — Mark unresponsive + Close as lost */}
            {!["won", "lost", "unresponsive"].includes(status) && (
                <div className="pt-4 border-t border-border" data-testid="b2b-resolve-bar">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                        Or resolve this lead
                    </div>
                    {showUnresponsive ? (
                        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 mb-3" data-testid="b2b-unresponsive-form">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="text-sm font-semibold text-rose-900 inline-flex items-center gap-1.5">
                                    <PhoneSlash size={15} weight="bold" /> Mark unresponsive
                                </div>
                                <button type="button" onClick={() => setShowUnresponsive(false)} className="text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="b2b-unresponsive-cancel">Cancel</button>
                            </div>
                            <p className="text-[12px] text-rose-900/80 mb-2">
                                Pauses the follow-up cadence. The lead remains in the queue but stops counting toward open work.
                            </p>
                            <Textarea
                                value={urNote}
                                onChange={(e) => setUrNote(e.target.value)}
                                rows={2}
                                placeholder="Anything worth noting before we pause? (optional)"
                                className="text-sm mb-2"
                                data-testid="b2b-unresponsive-note"
                            />
                            <div className="flex justify-end">
                                <Button size="sm" variant="destructive" onClick={submitUnresponsive} disabled={busy}
                                    data-testid="b2b-unresponsive-confirm">
                                    {busy ? "Marking…" : "Confirm unresponsive"}
                                </Button>
                            </div>
                        </div>
                    ) : showLost ? (
                        <div className="rounded-xl border border-rose-200 bg-rose-50/30 p-4 mb-3" data-testid="b2b-lost-form">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="text-sm font-semibold text-rose-900 inline-flex items-center gap-1.5">
                                    <X size={15} weight="bold" /> Close as lost
                                </div>
                                <button type="button"
                                    onClick={() => { setShowLost(false); setLossReason(null); setLossNote(""); }}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="b2b-lost-cancel">Cancel</button>
                            </div>
                            <Label className="text-xs">Pick a reason</Label>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="b2b-lost-reasons">
                                {LOSS_REASONS.map((r) => {
                                    const active = lossReason === r.id;
                                    return (
                                        <button key={r.id} type="button" onClick={() => setLossReason(r.id)}
                                            className={`inline-flex items-center gap-2 px-3 h-10 text-[12.5px] font-medium rounded-lg border text-left transition-all ${
                                                active ? "border-rose-400 bg-rose-100 text-rose-800 shadow-sm"
                                                       : "border-border bg-background text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                                            }`}
                                            data-testid={`b2b-lost-reason-${r.id}`}
                                            aria-pressed={active}>
                                            <r.icon size={13} weight="bold" />
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <Label className="text-xs mt-3 block">Extra context (optional)</Label>
                            <Textarea value={lossNote} onChange={(e) => setLossNote(e.target.value)} rows={2}
                                className="mt-1 text-sm" data-testid="b2b-lost-note"
                            />
                            <div className="flex justify-end mt-3">
                                <Button size="sm" variant="destructive" onClick={submitLost} disabled={lossBusy || !lossReason}
                                    data-testid="b2b-lost-confirm">
                                    {lossBusy ? "Closing…" : "Confirm close-lost"}
                                </Button>
                            </div>
                        </div>
                    ) : showIncomplete ? (
                        <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-4 mb-3" data-testid="b2b-incomplete-form">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="text-sm font-semibold text-amber-900 inline-flex items-center gap-1.5">
                                    <Warning size={15} weight="bold" /> Mark application incomplete
                                </div>
                                <button type="button" onClick={() => { setShowIncomplete(false); setMissDocs([]); setMissNote(""); setCustomDoc(""); }}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="b2b-incomplete-cancel">Cancel</button>
                            </div>
                            <p className="text-[12px] text-amber-900/80 mb-3">
                                Tick what&apos;s missing. We&apos;ll email the doctor a clear checklist + flip the lead to <em>application_incomplete</em>.
                            </p>
                            <Label className="text-xs mb-1.5 block">What&apos;s missing?</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3" data-testid="b2b-incomplete-doc-picker">
                                {COMMON_MISSING_DOCS.map((doc) => {
                                    const on = missDocs.includes(doc);
                                    return (
                                        <button key={doc} type="button" onClick={() => toggleDoc(doc)}
                                            className={`inline-flex items-center gap-2 px-3 h-10 text-[12.5px] font-medium rounded-lg border text-left transition-all ${
                                                on ? "border-amber-400 bg-amber-100 text-amber-900 shadow-sm"
                                                   : "border-border bg-background text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                                            }`}
                                            data-testid={`b2b-incomplete-doc-${doc.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                            aria-pressed={on}>
                                            <Plus size={11} weight="bold" />
                                            {doc}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <Input value={customDoc} onChange={(e) => setCustomDoc(e.target.value)}
                                    placeholder="Custom item (Enter to add)"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && customDoc.trim()) {
                                            e.preventDefault();
                                            if (!missDocs.includes(customDoc.trim())) toggleDoc(customDoc.trim());
                                            setCustomDoc("");
                                        }
                                    }}
                                    className="flex-1 text-sm"
                                    data-testid="b2b-incomplete-custom-input" />
                                <Button size="sm" variant="outline"
                                    disabled={!customDoc.trim()}
                                    onClick={() => { if (customDoc.trim() && !missDocs.includes(customDoc.trim())) { toggleDoc(customDoc.trim()); setCustomDoc(""); } }}
                                    data-testid="b2b-incomplete-add-custom">Add</Button>
                            </div>
                            <Label className="text-xs mt-1 block">Extra context (optional)</Label>
                            <Textarea value={missNote} onChange={(e) => setMissNote(e.target.value)} rows={2}
                                className="mt-1 text-sm" placeholder="e.g. 'Please send all updated docs by next Monday.'"
                                data-testid="b2b-incomplete-note" />
                            <div className="flex justify-end mt-3">
                                <Button size="sm"
                                    onClick={submitIncomplete}
                                    disabled={missBusy || missDocs.length === 0}
                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid="b2b-incomplete-confirm">
                                    {missBusy ? "Sending…" : `Email doctor (${missDocs.length} doc${missDocs.length === 1 ? "" : "s"})`}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {canMarkIncomplete && (
                                <Button size="sm" variant="outline"
                                    onClick={() => setShowIncomplete(true)}
                                    className="border-amber-200 text-amber-800 hover:bg-amber-50"
                                    data-testid="b2b-mark-incomplete-btn">
                                    <Warning size={13} className="mr-1.5" /> Mark incomplete
                                </Button>
                            )}
                            <Button size="sm" variant="outline"
                                onClick={() => setShowUnresponsive(true)}
                                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                data-testid="b2b-mark-unresponsive-btn">
                                <PhoneSlash size={13} className="mr-1.5" /> Mark unresponsive
                            </Button>
                            <Button size="sm" variant="outline"
                                onClick={() => setShowLost(true)}
                                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                data-testid="b2b-close-lost-btn">
                                <X size={13} className="mr-1.5" /> Close as lost
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Demo Scheduling (kept as a sub-component used inside Step 3)
// ──────────────────────────────────────────────────────────────────────────

const DEMO_FORMATS = [
    { id: "video",        label: "Video call (Zoom / Meet)" },
    { id: "in_person",    label: "In-person visit" },
    { id: "screen_share", label: "Screen share" },
];

function formatDemoLocal(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            weekday: "short", day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

export function DemoSchedulingPanel({ lead, busy, onSubmit }) {
    // iter98.99fb — For approved leads the same scheduling mechanic is
    // re-labelled as "Onboarding Kickoff" — different copy + default
    // agenda so the doctor reads it as an onboarding session rather
    // than a sales pitch.
    const isKickoff = lead.status === "approved";
    const titleLabel    = isKickoff ? "Schedule onboarding kickoff" : "Schedule demo";
    const subtitleLabel = isKickoff ? "Sends an .ics calendar invite (platform walkthrough + agreement)." : "Sends an .ics calendar invite to the doctor.";
    const confirmedLabel = isKickoff ? "Kickoff scheduled" : "Schedule demo";
    const submitButton   = isKickoff ? "Confirm & email kickoff invite" : "Confirm & email invite";
    const defaultAgenda = isKickoff
        ? "1. Welcome & quick platform tour\n2. How patient referrals will work\n3. Partner provider agreement\n4. Q&A + next steps"
        : "1. Quick intro to Invisique™ platform\n2. Pricing & onboarding flow\n3. Q&A";

    const scheduled = !!lead.demo_at;
    const [editing, setEditing] = useState(false);
    const [demoAt, setDemoAt] = useState("");
    const [demoFormat, setDemoFormat] = useState("video");
    const [agenda, setAgenda] = useState(defaultAgenda);

    const openForm = () => {
        if (lead.demo_at) {
            const d = new Date(lead.demo_at);
            const iso = d.toISOString().slice(0, 16);
            setDemoAt(iso);
            setDemoFormat(lead.demo_format || "video");
            setAgenda(lead.demo_agenda || defaultAgenda);
        } else {
            setDemoAt("");
            setDemoFormat("video");
            setAgenda(defaultAgenda);
        }
        setEditing(true);
    };

    const submit = () => {
        if (!demoAt) { toast.error("Pick a date and time"); return; }
        const isoFull = new Date(demoAt).toISOString();
        onSubmit({ demo_at: isoFull, demo_format: demoFormat, agenda: agenda.trim() });
        setEditing(false);
    };

    return (
        <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/30 p-4" data-testid="b2b-demo-panel">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Calendar size={17} weight="duotone" className="text-fuchsia-700" />
                    <div>
                        <div className="text-xs uppercase tracking-wide font-bold text-fuchsia-900">{titleLabel}</div>
                        <div className="text-[11px] text-fuchsia-900/70">{subtitleLabel}</div>
                    </div>
                </div>
                {scheduled && (
                    <span className="text-[10px] uppercase tracking-wide font-bold bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <CheckCircle size={11} weight="fill" /> Confirmed
                    </span>
                )}
            </div>

            {scheduled && !editing && (
                <div className="space-y-2" data-testid="b2b-demo-confirmed">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-fuchsia-800">When</div>
                        <div className="text-sm font-semibold text-fuchsia-900 mt-0.5" data-testid="b2b-demo-when">
                            {formatDemoLocal(lead.demo_at)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-fuchsia-800">Format</div>
                        <div className="text-sm text-fuchsia-900 mt-0.5">
                            {DEMO_FORMATS.find((f) => f.id === lead.demo_format)?.label || lead.demo_format || "—"}
                        </div>
                    </div>
                    {lead.demo_agenda && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-fuchsia-800">Agenda</div>
                            <pre className="text-xs text-fuchsia-900 whitespace-pre-wrap font-sans mt-0.5">{lead.demo_agenda}</pre>
                        </div>
                    )}
                    <Button variant="outline" size="sm" onClick={openForm}
                        className="mt-2 border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100"
                        data-testid="b2b-demo-reschedule-btn">
                        Reschedule
                    </Button>
                </div>
            )}

            {!scheduled && !editing && (
                <Button onClick={openForm}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="b2b-demo-schedule-btn">
                    <Calendar size={14} weight="fill" className="mr-1.5" /> {titleLabel}
                </Button>
            )}

            {editing && (
                <div className="space-y-3" data-testid="b2b-demo-form">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-fuchsia-900 inline-flex items-center gap-1.5">
                            <Calendar size={14} weight="fill" /> Schedule the demo
                        </div>
                        <button type="button" onClick={() => setEditing(false)}
                            className="text-xs text-muted-foreground hover:text-foreground"
                            data-testid="b2b-demo-cancel">Cancel</button>
                    </div>
                    <div>
                        <Label className="text-xs">Date &amp; time (your timezone)</Label>
                        <input type="datetime-local" value={demoAt} onChange={(e) => setDemoAt(e.target.value)}
                            className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            data-testid="b2b-demo-at" />
                    </div>
                    <div>
                        <Label className="text-xs">Format</Label>
                        <select value={demoFormat} onChange={(e) => setDemoFormat(e.target.value)}
                            className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            data-testid="b2b-demo-format">
                            {DEMO_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Agenda (sent to the doctor)</Label>
                        <Textarea value={agenda} onChange={(e) => setAgenda(e.target.value)} rows={4}
                            className="mt-1 text-sm" data-testid="b2b-demo-agenda" />
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={submit} disabled={busy || !demoAt}
                            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                            data-testid="b2b-demo-submit">
                            {busy ? "Scheduling…" : <><Calendar size={13} className="mr-1.5" weight="fill" /> {submitButton}</>}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Application invite (compact)
// ──────────────────────────────────────────────────────────────────────────

function InvitePanel({ lead, onSend }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const alreadySent = !!lead.invite_emailed_at;
    const expires = lead.invite_token_expires_at ? new Date(lead.invite_token_expires_at) : null;
    const isExpired = expires && expires < new Date();
    const applyUrl = lead.invite_token ? `/apply-as-provider/${lead.invite_token}` : null;
    const absoluteUrl = applyUrl ? `${window.location.origin}${applyUrl}` : null;

    const send = async () => {
        setBusy(true);
        await onSend?.({ note });
        setNote("");
        setBusy(false);
    };

    const copyUrl = async () => {
        if (!absoluteUrl) return;
        try { await navigator.clipboard.writeText(absoluteUrl); toast.success("Link copied"); }
        catch { toast.error("Couldn't copy — select manually"); }
    };

    return (
        <div className="rounded-xl border border-cyan-200 bg-cyan-50/30 p-4" data-testid="b2b-invite-panel">
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <PaperPlaneTilt size={17} weight="duotone" className="text-cyan-800" />
                    <div>
                        <div className="text-xs uppercase tracking-wide font-bold text-cyan-900">Application invite</div>
                        <div className="text-[11px] text-cyan-900/70">14-day magic link to the full ProviderApply wizard.</div>
                    </div>
                </div>
                {alreadySent && !isExpired && (
                    <span className="text-[10px] uppercase tracking-wide font-bold bg-cyan-100 text-cyan-700 border border-cyan-200 px-2 py-0.5 rounded-full">
                        Sent
                    </span>
                )}
                {isExpired && (
                    <span className="text-[10px] uppercase tracking-wide font-bold bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full">
                        Expired
                    </span>
                )}
            </div>
            <Label className="text-xs">Personal note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="e.g. 'Great chatting today — looking forward to onboarding your clinic.'"
                className="mt-1 mb-3 text-sm"
                data-testid="b2b-invite-note-input" />
            <div className="flex flex-wrap items-center gap-2">
                <Button onClick={send} disabled={busy} size="sm" className="rounded-full"
                    data-testid="b2b-send-invite-btn">
                    <PaperPlaneTilt size={13} className="mr-1.5" weight="fill" />
                    {busy ? "Sending…" : (alreadySent ? "Re-send invite" : "Send invite")}
                </Button>
                {absoluteUrl && !isExpired && (
                    <button onClick={copyUrl}
                        className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-secondary"
                        data-testid="b2b-copy-invite-url-btn">
                        <Copy size={12} /> Copy link
                    </button>
                )}
            </div>
            {absoluteUrl && !isExpired && (
                <div className="mt-2 text-[11px] font-mono text-muted-foreground break-all bg-background rounded px-2 py-1.5 border border-border"
                    data-testid="b2b-invite-url">
                    <LinkSimple size={11} className="inline mr-1" /> {absoluteUrl}
                </div>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Tasks
// ──────────────────────────────────────────────────────────────────────────

function TasksPanel({ leadId, tasks, onChange }) {
    const [title, setTitle] = useState("");
    const [due, setDue] = useState("");
    const [busy, setBusy] = useState(false);

    const open = tasks.filter((t) => t.status === "open");
    const done = tasks.filter((t) => t.status === "done");

    const add = async () => {
        if (!title.trim()) return;
        setBusy(true);
        try {
            await api.post(`/sales/b2b-leads/${leadId}/tasks`, {
                title: title.trim(),
                due_at: due ? new Date(due).toISOString() : null,
            });
            setTitle("");
            setDue("");
            await onChange?.();
            toast.success("Task added");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't add task");
        } finally {
            setBusy(false);
        }
    };

    const complete = async (taskId) => {
        try {
            await api.put(`/sales/b2b-leads/${leadId}/tasks/${taskId}/complete`);
            await onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't complete");
        }
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="b2b-tasks-panel">
            <div className="flex items-center gap-2 mb-4">
                <ListChecks size={18} weight="duotone" className="text-primary" />
                <h3 className="font-display text-base font-semibold">Follow-up tasks</h3>
                <span className="text-[11px] text-muted-foreground">Personal reminders for this lead.</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="b2b-task-composer">
                <Input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Call Tuesday 4pm to confirm decision"
                    className="flex-1 min-w-[220px] text-sm"
                    data-testid="b2b-task-title" />
                <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)}
                    className="text-sm w-[200px]"
                    data-testid="b2b-task-due" />
                <Button onClick={add} disabled={busy || !title.trim()} size="sm"
                    data-testid="b2b-task-add">
                    <Plus size={13} className="mr-1" /> Add
                </Button>
            </div>

            {open.length === 0 && done.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="b2b-tasks-empty">
                    No tasks yet. Add a reminder above so this lead doesn&apos;t slip.
                </div>
            ) : (
                <>
                    {open.length > 0 && (
                        <ul className="space-y-2 mb-3" data-testid="b2b-tasks-open">
                            {open.map((t) => (
                                <li key={t.id} className="flex items-center gap-3 rounded-lg border border-border p-3"
                                    data-testid={`b2b-task-${t.id}`}>
                                    <button onClick={() => complete(t.id)}
                                        className="w-5 h-5 rounded-full border-2 border-muted-foreground/40 hover:border-emerald-500 grid place-items-center transition-colors"
                                        title="Mark done"
                                        data-testid={`b2b-task-${t.id}-complete`}>
                                        {/* empty circle */}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{t.title}</div>
                                        {t.due_at && (
                                            <div className={`text-[11px] mt-0.5 inline-flex items-center gap-1 ${
                                                new Date(t.due_at) < new Date() ? "text-rose-700 font-semibold" : "text-muted-foreground"
                                            }`}>
                                                <Clock size={10} /> Due {new Date(t.due_at).toLocaleString("en-IN", {
                                                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {done.length > 0 && (
                        <details className="text-xs text-muted-foreground" data-testid="b2b-tasks-done">
                            <summary className="cursor-pointer hover:text-foreground">{done.length} completed task{done.length === 1 ? "" : "s"}</summary>
                            <ul className="space-y-1 mt-2 pl-3">
                                {done.map((t) => (
                                    <li key={t.id} className="flex items-center gap-2">
                                        <CheckCircle size={11} weight="fill" className="text-emerald-600" />
                                        <span className="line-through">{t.title}</span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Meta footer
// ──────────────────────────────────────────────────────────────────────────

function MetaFooter({ lead }) {
    const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
    return (
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 px-2"
            data-testid="b2b-meta-footer">
            <span>Created {fmtDate(lead.created_at)}</span>
            <span>Updated {fmtDate(lead.updated_at)}</span>
            <span>Assigned: {lead.assigned_sales_name || "—"}</span>
            {lead.invite_emailed_at && <span>Invite sent {fmtDate(lead.invite_emailed_at)}</span>}
            {lead.doctor_id && <span>Doctor ID · {lead.doctor_id}</span>}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Admin-only reassign dialog (unchanged from previous iter)
// ──────────────────────────────────────────────────────────────────────────

function ReassignDialog({ lead, onClose, onDone }) {
    const [reps, setReps] = useState([]);
    const [picked, setPicked] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/staff").then((r) => {
            setReps((r.data || []).filter((s) => s.staff_type === "sales" && s.is_active !== false));
        }).catch(() => setReps([]));
    }, []);

    const submit = async () => {
        if (!picked) return toast.error("Pick a sales rep");
        setBusy(true);
        try {
            await api.put(`/admin/b2b-leads/${lead.id}/reassign`, { sales_id: picked });
            toast.success("Lead reassigned");
            onDone?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reassign");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-foreground/50 z-50 grid place-items-center p-5" onClick={onClose}
            data-testid="b2b-reassign-dialog">
            <div className="bg-card rounded-2xl border border-border shadow-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg font-bold">Reassign lead</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground"
                        data-testid="b2b-reassign-close-btn">
                        <X size={18} />
                    </button>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Currently assigned to <strong className="text-foreground">{lead.assigned_sales_name || "—"}</strong>. Reassigning keeps all notes + status history.
                </p>
                <Label className="text-xs mb-1.5 block">Sales rep</Label>
                <select value={picked} onChange={(e) => setPicked(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mb-4"
                    data-testid="b2b-reassign-select">
                    <option value="">Select…</option>
                    {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="b2b-reassign-cancel-btn">Cancel</Button>
                    <Button onClick={submit} disabled={busy || !picked} className="rounded-full" data-testid="b2b-reassign-submit-btn">
                        {busy ? "Reassigning…" : "Reassign"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Application panel (provider_application linked to this lead)
// ──────────────────────────────────────────────────────────────────────────

const APP_STATUS_PILL = {
    pending:  { label: "Pending review", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    approved: { label: "Approved",       cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    rejected: { label: "Rejected",       cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

const SPECIALTY_DISPLAY = {
    Orthodontist: "Orthodontist",
    GP_Dentist: "GP / General Dentist",
    Pedodontist: "Pedodontist",
    Prosthodontist: "Prosthodontist",
    Other: "Other",
};

function ApplicationPanel({ application: a, doctorMessage }) {
    /* iter98.99fi — Stripped of all decision UI. Accept/Reject + the
       acceptance gate now live inside the Step 1 panel (Decide & move
       sub-section). This is purely a read-only "source of truth" card so
       the PES sees the doctor's data BEFORE working through Step 1.
       Doctor's note (the b2b_lead.message field) renders inside this
       card so it gets a clear label and stops floating awkwardly.

       iter98.99fd — Deduplicated against the header:
       · Specialty + Cases/Month removed from the grid because the header
         already carries them (role chip + Cases/Mo intel cell). Grid is
         now 4 application-specific credentials: Degree · Years · Reg# ·
         State Council.
       · Doctor's note is only rendered when it materially differs from
         why_invisique (otherwise it's the exact same string showing twice
         in the same panel — direct-apply leads commonly populate both
         from the same form field). */
    const status = a.status || "pending";
    const pill = APP_STATUS_PILL[status] || APP_STATUS_PILL.pending;
    const noteIsDupOfWhy = doctorMessage && a.why_invisique
        && doctorMessage.trim().toLowerCase() === a.why_invisique.trim().toLowerCase();
    const showDoctorNote = !!doctorMessage && !noteIsDupOfWhy;

    return (
        <div className="bg-card border border-primary/30 rounded-2xl p-5 ring-1 ring-primary/10"
            data-testid="b2b-application-panel">
            <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Briefcase size={18} weight="duotone" className="text-primary" />
                    <span className="text-xs uppercase tracking-wide font-bold">Application submitted</span>
                    {a.code && (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-secondary/60 text-muted-foreground border border-border"
                            data-testid="b2b-application-code">
                            {a.code}
                        </span>
                    )}
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold ${pill.cls}`}
                    data-testid={`b2b-application-status-${status}`}>
                    {pill.label}
                </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm mb-4">
                <Row icon={GraduationCap} label="Degree" value={a.degree || "—"} />
                <Row label="Years in practice" value={a.years_in_practice != null ? `${a.years_in_practice}` : "—"} />
                <Row icon={IdentificationBadge} label="Council reg. #" value={a.council_registration_number || "—"} mono />
                <Row label="State council" value={a.state_council || "—"} />
            </div>

            {a.why_invisique && (
                <div className="rounded-xl bg-secondary/30 border border-border/60 p-4 mb-3">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Why Invisique™</div>
                    <p className="text-sm whitespace-pre-wrap">{a.why_invisique}</p>
                </div>
            )}

            {showDoctorNote && (
                <div className="rounded-xl bg-amber-50/40 border border-amber-200 p-4" data-testid="b2b-doctor-note">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-amber-900 mb-1 flex items-center gap-1">
                        <ChatCenteredText size={11} weight="fill" /> Doctor&apos;s note (from request form)
                    </div>
                    <p className="text-sm whitespace-pre-wrap text-amber-950">{doctorMessage}</p>
                </div>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Tiny atoms (shared)
// ──────────────────────────────────────────────────────────────────────────

function Row({ icon: Icon, label, value, mono }) {
    return (
        <div>
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5 flex items-center gap-1">
                {Icon && <Icon size={11} />} {label}
            </dt>
            <dd className={mono ? "font-mono text-sm" : "text-sm"}>{value || "—"}</dd>
        </div>
    );
}
